import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  initiateSession,
  startPolling,
  stopPolling,
  setSelectedAccount,
  clearError,
  getIsInitiating,
  getError,
  getBankClaimStatus,
  getBankAccounts,
  getBankClaimId,
  getHasToken,
} from 'redux/modules/bankPayment';
import {
  generate as generateVoucher,
  getIsGenerating,
  getRecoverableError,
  clearError as clearVoucherError,
} from 'redux/modules/voucher';
import { getHuggg } from 'redux/modules/huggg';
import { getSelectedSegment } from 'redux/modules/selectedSegment';
import { getRedemptionMethodID } from 'utilities/redemption';
import { ConnectBankView } from './views/ConnectBankView';
import { WaitingForConsentView } from './views/WaitingForConsentView';
import { AccountPickerView } from './views/AccountPickerView';
import { RetryView } from './views/RetryView';
import styles from './bankPaymentRedemption.module.scss';

const LANG = 'en';
const COUNTRY = 'GB';

const buildTokenSessionUrl = (redirectUrl) => {
  const url = new URL(redirectUrl);
  url.searchParams.set('lang', LANG);
  url.searchParams.set('country', COUNTRY);
  return url.toString();
};

export const BankPaymentRedemptionUnconnected = (props) => {
  const {
    huggg,
    selectedSegment,
    status,
    accounts,
    bankClaimId,
    hasToken,
    isInitiating,
    error,
    isGenerating,
    recoverableError,
    initiateSession,
    startPolling,
    stopPolling,
    setSelectedAccount,
    generateVoucher,
    clearError,
    clearVoucherError,
  } = props;

  useEffect(() => {
    startPolling(huggg.id);
    return () => stopPolling();
  }, [huggg.id, startPolling, stopPolling]);

  const onConnectBank = useCallback(() => {
    if (error) clearError();
    return initiateSession(huggg.id)
      .then(({ redirectUrl }) => {
        window.location.assign(buildTokenSessionUrl(redirectUrl));
      })
      .catch(() => {
        // Error already in Redux state; the active view renders it.
      });
  }, [huggg.id, error, initiateSession, clearError]);

  const onSelectAccount = useCallback(
    (account) => {
      if (recoverableError) clearVoucherError();
      setSelectedAccount(account);
      return generateVoucher(
        huggg.id,
        selectedSegment && selectedSegment.id,
        getRedemptionMethodID(huggg),
        {
          bankClaimId,
          accountNumber: account.accountNumber,
          sortCode: account.sortCode,
        },
      );
    },
    [
      huggg,
      selectedSegment,
      bankClaimId,
      recoverableError,
      setSelectedAccount,
      generateVoucher,
      clearVoucherError,
    ],
  );

  switch (status) {
    case null:
    case undefined:
      return (
        <ConnectBankView
          onConnect={onConnectBank}
          isLoading={isInitiating}
          error={error}
          onDismissError={clearError}
        />
      );

    case 'INITIATED':
      return (
        <WaitingForConsentView
          onRelaunch={onConnectBank}
          isLoading={isInitiating}
          error={error}
        />
      );

    case 'CALLBACK_RECEIVED':
      return (
        <AccountPickerView
          accounts={accounts || []}
          isLoadingAccounts={!accounts || (hasToken && accounts.length === 0)}
          isClaiming={isGenerating}
          recoverableError={recoverableError}
          onSelectAccount={onSelectAccount}
        />
      );

    case 'COMPLETED':
      // Server-side claim succeeded; the voucher pipeline has already refetched
      // the huggg, so the parent will switch to VoucherClaimed on the next render.
      // Render a brief spinner to bridge that interval.
      return (
        <div
          className={styles.container}
          data-test-id="bank-payment-completed-view"
        >
          <div
            className={styles.spinner}
            data-test-id="bank-payment-completed-spinner"
          />
        </div>
      );

    case 'FAILED':
      return (
        <RetryView
          onRetry={onConnectBank}
          isLoading={isInitiating}
          error={error}
        />
      );

    default:
      return null;
  }
};

BankPaymentRedemptionUnconnected.propTypes = {
  huggg: PropTypes.object.isRequired,
  selectedSegment: PropTypes.object,
  status: PropTypes.string,
  accounts: PropTypes.array,
  bankClaimId: PropTypes.string,
  hasToken: PropTypes.bool,
  isInitiating: PropTypes.bool,
  error: PropTypes.any,
  isGenerating: PropTypes.bool,
  recoverableError: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
  }),
  initiateSession: PropTypes.func.isRequired,
  startPolling: PropTypes.func.isRequired,
  stopPolling: PropTypes.func.isRequired,
  setSelectedAccount: PropTypes.func.isRequired,
  generateVoucher: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  clearVoucherError: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  huggg: getHuggg(state),
  selectedSegment: getSelectedSegment(state),
  status: getBankClaimStatus(state),
  accounts: getBankAccounts(state),
  bankClaimId: getBankClaimId(state),
  hasToken: getHasToken(state),
  isInitiating: getIsInitiating(state),
  error: getError(state),
  isGenerating: getIsGenerating(state),
  recoverableError: getRecoverableError(state),
});

export const BankPaymentRedemption = connect(mapStateToProps, {
  initiateSession,
  startPolling,
  stopPolling,
  setSelectedAccount,
  generateVoucher,
  clearError,
  clearVoucherError,
})(BankPaymentRedemptionUnconnected);

export default BankPaymentRedemption;
